export default {
  singleLabel: "Attribut de demande",
  storeNamespace: 'request_attribute',
  authFeature: 'location-for-account',
  listRouteName: "RequestAttributeList",
  newRouteName: "RequestAttributeNew",
  editRouteName: "RequestAttributeEdit",
  useNewModal: true,
  useEditModal: true,
  formComponentName: 'RequestAttributeForm',
  csvExportFilenamePrefix: 'attributs-de-demande',
  titleField: 'label',
  sortBy: 'label',
  fields: [
    {
      name: 'label',
      label: "Libellé",
      type: 'text',
      inForm: true,
      required: true,
      inList: true,
      sortable: true,
      filterIn: true,
      inDetail: true,
      inCsvExport: true,
    },
    {
      name: 'field_type',
      label: "Type de champ",
      type: 'select',
      inForm: true,
      required: true,
      inList: true,
      sortable: true,
      extendedFilter: true,
      filterOrder: 2,
      inDetail: true,
      inCsvExport: true,
      formatter: (context, value) => value ? context.$store.getters['request_attribute/getFieldTypeLabel'](value) : null,
      options: ($store) => $store.getters['request_attribute/getFieldTypesForSelect'],
    },
    {
      // Le rattachement classe la bibliothèque, il ne remplace pas l'activation — qui reste
      // au niveau du produit. Avec cent soixante attributs, retrouver ceux d'une nacelle sans
      // pouvoir filtrer par famille relève de la fouille.
      //
      // Plusieurs familles sur un même attribut : « Type d'énergie » se pose dans les mêmes
      // termes pour une nacelle, un compacteur et un groupe électrogène. Un rattachement
      // unique obligeait à en tenir une copie par famille, et donc à corriger une valeur
      // proposée autant de fois qu'il y avait de copies.
      name: 'equipment_group_ids',
      label: "Familles",
      description: "Une ou plusieurs. Sur une famille racine, l'attribut est proposé à toutes ses sous-familles.",
      type: 'chosen',
      multiple: true,
      placeholder: "Sélectionnez une ou plusieurs familles…",
      inForm: true,
      inList: false,
      inDetail: false,
    },
    {
      name: 'equipment_group_paths',
      label: "Familles",
      type: 'autocomplete',
      filterComponent: 'OptionsAutocomplete',
      filterPlaceholder: "Recherchez une famille…",
      inForm: false,
      inList: true,
      sortable: true,
      extendedFilter: true,
      filterOrder: 1,
      inDetail: true,
      inCsvExport: true,
      // `equipment_group_paths` donne « Travail en hauteur › Nacelle » : la racine seule ne
      // suffit pas à situer l'attribut, la sous-famille seule non plus. Les chemins arrivent
      // triés du serveur ; les joindre ici les rend triables et exportables comme une colonne
      // de texte ordinaire.
      formatter: (context, value) => Array.isArray(value) && value.length ? value.join(' · ') : '—',
      // Filtrer sur une famille racine doit remonter ses sous-familles : les attributs sont
      // majoritairement rattachés au niveau fin (Nacelle, Échafaudage…), et personne ne
      // cherche « les attributs de Nacelle » sans passer par « Travail en hauteur ».
      // `equipment_group_root_ids` est calculé côté serveur : la relation parente n'est pas
      // sérialisée, la profondeur maximale la coupe avant.
      filter: (context, filterValue, itemValue, item) => {
        const groupIds = item.equipment_group_ids || [];

        if (filterValue === 'none') {
          return !groupIds.length;
        }

        return (item.equipment_group_root_ids || []).some((id) => id == filterValue)
          || groupIds.some((id) => id == filterValue);
      },
    },
    {
      name: 'unit',
      label: "Unité",
      type: 'text',
      inForm: true,
      inList: true,
      sortable: true,
      inDetail: true,
      inCsvExport: true,
      // Une unité n'a de sens que sur une valeur numérique.
      inFormCondition: (form) => form.field_type == 'number' || form.field_type == 'boolean_number',
      formatter: (context, value) => value || '',
    },
    {
      name: 'choices',
      label: "Valeurs proposées",
      // Des tags plutôt qu'un textarea : une valeur proposée est une unité, pas une ligne de
      // texte. On la voit, on la retire, on la réordonne sans compter les retours chariot.
      // Le stockage, lui, ne bouge pas — l'API attend toujours une valeur par ligne
      // (cf. `submitFormatter` plus bas et `RequestAttribute::getChoiceList()`).
      type: 'tags',
      inForm: true,
      inDetail: true,
      // Un choix unique ou multiple sans valeur ne s'affiche pas : le champ n'apparaît que
      // pour ces deux types, et devient alors obligatoire. Un « Oui / Non » porte ses
      // réponses dans le code, il n'a rien à saisir ici.
      inFormCondition: (form) => form.field_type == 'multiple_choice' || form.field_type == 'radio',
      requiredCondition: (form) => form.field_type == 'multiple_choice' || form.field_type == 'radio',
      formatter: (context, value) => value ? value.split("\n").join(" · ") : null,
    },
    {
      name: 'required',
      label: "Réponse obligatoire",
      type: 'yesno',
      inForm: true,
      inList: true,
      inDetail: true,
      inCsvExport: true,
    },
    {
      name: 'active',
      label: "Actif",
      type: 'yesno',
      inForm: true,
      inList: true,
      inDetail: true,
      inCsvExport: true,
      // Désactiver plutôt que supprimer : un attribut retiré doit rester lisible sur les
      // demandes passées qui portent ses valeurs.
    },
  ],
  /**
   * Le champ « Valeurs proposées » travaille en tags (`[{text}]`), l'API en texte, une valeur
   * par ligne. La conversion se fait ici, au moment de l'envoi : le format de stockage reste
   * celui que `RequestAttribute::getChoiceList()` sait lire, et le back continue de trimer et
   * dédoublonner de son côté.
   *
   * Une liste vide part en chaîne vide plutôt qu'en null : le back n'écrit le champ que s'il
   * est présent, et c'est ce qui permet de purger les valeurs d'un attribut repassé en texte.
   */
  submitFormatter: function (form) {
    return {
      ...form,
      choices: Array.isArray(form.choices)
        ? form.choices.map((tag) => tag.text).join("\n")
        : (form.choices || ''),
      // Chosen rend des chaînes, et `null` quand plus rien n'est sélectionné. Le back reçoit
      // toujours un tableau : c'est l'état complet du rattachement, une famille retirée doit
      // se voir. D'où le tableau vide plutôt qu'une clé absente, qui ne changerait rien.
      equipment_group_ids: Array.isArray(form.equipment_group_ids)
        ? form.equipment_group_ids.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id))
        : [],
    }
  },
}



// WEBPACK FOOTER //
// ./src/components/RequestAttribute/config.js